import { useState, useRef, useEffect } from "react";
import {
  FileDownloadOutlined,
  RestartAltOutlined,
  CalendarMonth,
  ArrowForwardRounded,
} from "@mui/icons-material";
import dayjs from "dayjs";
import { exportPredictions } from "../../../../services/Dashboard/Table";
import { formatDateOnly } from "../../../../Utils/formatDateTime";

export default function PredictionDataExcelExport() {
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const fromDateRef = useRef(null);
  const toDateRef = useRef(null);

  // Auto-hide success message after 10 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage("");
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Auto-hide error message after 10 seconds
  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => {
        setErrorMessage("");
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  const handleFromDateChange = (value) => {
    setFromDate(value || null);
    // If new from-date is after current to-date, reset to-date
    if (toDate && value && value > toDate) {
      setToDate(null);
    }
    setSuccessMessage("");
    setErrorMessage("");
  };

  const handleToDateChange = (value) => {
    setToDate(value || null);
    setSuccessMessage("");
    setErrorMessage("");
  };

  const handleReset = () => {
    setFromDate(null);
    setToDate(null);
    setSuccessMessage("");
    setErrorMessage("");
  };

  const handleExport = async () => {
    if (!fromDate) return;

    // If no toDate is chosen, export just the single selected day
    const effectiveToDate = toDate ?? fromDate;

    setExporting(true);
    setSuccessMessage("");
    setErrorMessage("");

    try {
      await exportPredictions(fromDate, effectiveToDate);
      const isSingleDay = fromDate === effectiveToDate;
      setSuccessMessage(
        isSingleDay ? (
          <span>
            Excel file downloaded successfully for {formatDateOnly(fromDate)}.
          </span>
        ) : (
          <span className="inline-flex items-center gap-2 flex-wrap">
            Excel file downloaded successfully for {formatDateOnly(fromDate)}{" "}
            <ArrowForwardRounded sx={{ fontSize: 20 }} />{" "}
            {formatDateOnly(effectiveToDate)}.
          </span>
        )
      );
    } catch (err) {
      setErrorMessage(
        err?.error || err?.detail || "Failed to export predictions. Please try again."
      );
    } finally {
      setExporting(false);
    }
  };

  const isExportDisabled = !fromDate || exporting;

  return (
    <div className="grid gap-4">
      <div className="flex items-center justify-between gap-3 flex-wrap p-4 border border-[var(--color-border)] rounded-[var(--radius-md)] bg-[--color-surface]">
        <div className="flex flex-col gap-1">
          <p className="md:text-sm text-xs label-caps block m-0">Data Export</p>
          <h4 className="xl:text-xl md:text-lg text-base text-[var(--color-text)]">
            Export Prediction Data
          </h4>
          <p className="xl:text-base md:text-sm text-xs text-[var(--color-text-muted)]">
            Download a full prediction report including hot picks as a single Excel file with two sheets.
          </p>
        </div>
        <button
          type="button"
          onClick={handleReset}
          disabled={exporting}
          className="inline-flex items-center gap-1.5 min-h-[38px] px-3 py-2.5 border border-[var(--color-border)] rounded-[var(--radius-md)] bg-transparent text-[var(--color-text)] cursor-pointer md:text-sm text-xs font-bold disabled:cursor-not-allowed"
        >
          <RestartAltOutlined sx={{ fontSize: 17 }} />
          Reset
        </button>
      </div>

      {successMessage && (
        <div className="flex items-center gap-2 px-4 py-3.5 border border-[rgba(200,255,0,0.25)] bg-[rgba(200,255,0,0.12)] rounded-[var(--radius-md)] text-[var(--color-primary)] sm:text-sm text-xs">
          {successMessage}
        </div>
      )}

      {errorMessage && (
        <div className="px-4 py-3.5 border border-[rgba(255,69,0,0.25)] bg-[rgba(255,69,0,0.12)] rounded-[var(--radius-md)] text-[var(--color-accent-red)] md:text-sm text-xs">
          {errorMessage}
        </div>
      )}

      <div className="grid gap-6 p-4 border border-[var(--color-border)] rounded-[var(--radius-md)] bg-[var(--color-surface)]">
        <div className="grid md:grid-cols-2 gap-4">
          <div className="grid gap-2 text-[var(--color-text-muted)] md:text-sm text-xs font-bold uppercase tracking-[0.04em]">
            <span>From Date</span>
            <div
              onClick={() => {
                try {
                  fromDateRef.current?.showPicker();
                } catch (err) {
                  console.warn("showPicker failed, falling back to click:", err);
                }
              }}
              className="relative flex items-center gap-2.5 p-4 border border-[var(--color-border)] rounded-[var(--radius-md)] bg-[var(--color-bg)] cursor-pointer"
            >
              <CalendarMonth
                className="text-[var(--color-primary)] flex-shrink-0"
                sx={{ fontSize: 24 }}
              />
              <div className="flex items-center gap-2 min-w-0">
                <span className="md:text-sm text-xs label-caps block m-0 !text-[--color-text] !font-bold">
                  {fromDate ? formatDateOnly(fromDate) : "Select date"}
                </span>
              </div>
              <input
                ref={fromDateRef}
                type="date"
                value={fromDate ? dayjs(fromDate).format("YYYY-MM-DD") : ""}
                onChange={(e) => handleFromDateChange(e.target.value || null)}
                className="absolute inset-0 opacity-0 cursor-pointer w-full h-full [color-scheme:dark]"
              />
            </div>
          </div>
          <div className="grid gap-2 text-[var(--color-text-muted)] md:text-sm text-xs font-bold uppercase tracking-[0.04em]">
            <span>To Date</span>
            <div
              onClick={() => {
                if (fromDate) {
                  try {
                    toDateRef.current?.showPicker();
                  } catch (err) {
                    console.warn("showPicker failed, falling back to click:", err);
                  }
                }
              }}
              className={`relative flex items-center gap-2.5 p-4 border border-[var(--color-border)] rounded-[var(--radius-md)] bg-[var(--color-bg)] ${fromDate ? "cursor-pointer" : "cursor-not-allowed opacity-50"}`}
            >
              <CalendarMonth
                className="text-[var(--color-primary)] flex-shrink-0"
                sx={{ fontSize: 24 }}
              />
              <div className="flex items-center gap-2 min-w-0">
                <span
                  className={`md:text-sm text-xs label-caps block m-0 ${fromDate ? "!text-[--color-text]" : "!text-[var(--color-text-muted)]"} !font-bold`}
                >
                  {toDate ? formatDateOnly(toDate) : fromDate ? "Select date" : "—"}
                </span>
              </div>
              <input
                ref={toDateRef}
                type="date"
                value={toDate ? dayjs(toDate).format("YYYY-MM-DD") : ""}
                min={fromDate ? dayjs(fromDate).format("YYYY-MM-DD") : undefined}
                disabled={!fromDate}
                onChange={(e) => handleToDateChange(e.target.value || null)}
                className="absolute inset-0 opacity-0 cursor-pointer w-full h-full [color-scheme:dark] disabled:cursor-not-allowed"
              />
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={handleExport}
          disabled={isExportDisabled}
          className="inline-flex items-center justify-center justify-self-start gap-2 min-h-[42px] px-3 py-2.5 border border-[var(--color-primary)] rounded-[var(--radius-md)] bg-[var(--color-primary)] text-black md:text-sm text-xs font-black cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <FileDownloadOutlined sx={{ fontSize: 18 }} />
          {exporting ? "Exporting…" : "Export to Excel"}
        </button>
      </div>
    </div>
  );
}
